import type { Building } from "building-types/building/buildingTypes";
import { isBuildingEntityOfType, type BuildingEntity } from "building-types/entities/buildingEntity";
import {
  isBuildingEntityHandleOfType,
  type BuildingEntityHandle,
  type BuildingEntityHandleBase,
} from "building-types/entities/buildingEntityHandle";
import { ALL_BUILDING_ENTITY_TYPES, type BuildingEntityType } from "building-types/entities/buildingEntityTypeTypes";
import { buildingGetEntity } from "./buildingGetEntity";
import { buildingEntityTypeIsTableBased, entityTypeToTableName } from "./buildingTableBasedEntity";

export default function buildingForEachEntity(
  building: Building,
  callback: (entity: BuildingEntity, handle: BuildingEntityHandle) => void,
  handleTypes: readonly BuildingEntityType[] = ALL_BUILDING_ENTITY_TYPES,
): void {
  for (const entityType of handleTypes) {
    buildingForEachEntityHandleOfType(building, entityType, (handle) => {
      const entity = buildingGetEntity(building, handle);
      if (!entity) {
        return;
      }
      callback(entity, handle);
    });
  }
}

export function buildingForEachEntityHandle(
  building: Building,
  callback: (handle: BuildingEntityHandle) => void,
  handleTypes: readonly BuildingEntityType[] = ALL_BUILDING_ENTITY_TYPES,
): void {
  for (const entityType of handleTypes) {
    buildingForEachEntityHandleOfType(building, entityType, callback);
  }
}

export function buildingForEachEntityOfType<T extends BuildingEntityType>(
  building: Building,
  entityType: T,
  callback: (entity: Extract<BuildingEntity, { entityType: T }>, handle: BuildingEntityHandleBase<T>) => void,
): void {
  buildingForEachEntityHandleOfType(building, entityType, (handle) => {
    if (!isBuildingEntityHandleOfType(handle, entityType)) {
      return;
    }
    const entity = buildingGetEntity(building, handle);
    if (!entity) {
      return;
    }
    if (!isBuildingEntityOfType(entity, entityType)) {
      return;
    }
    callback(entity, handle);
  });
}

export function buildingForEachEntityHandleOfType<T extends BuildingEntityType>(
  building: Building,
  entityType: T,
  callback: (handle: BuildingEntityHandle) => void,
): void {
  if (buildingEntityTypeIsTableBased(entityType)) {
    const tableName = entityTypeToTableName[entityType];
    const table = building[tableName];
    if (!table) {
      return;
    }

    for (const id in table) {
      callback({ entityType, id } as BuildingEntityHandle);
    }
  }

  // Handle non-table based entity types
  switch (entityType) {
    case "INTERIOR_TRIM":
      // interior trim lives in two tables
      const baseTrimMoldings = building.baseTrimMoldings;
      if (baseTrimMoldings) {
        for (const id in baseTrimMoldings) {
          callback({ entityType, id } as BuildingEntityHandle);
        }
      }
      const crownTrimMoldings = building.crownTrimMoldings;
      if (crownTrimMoldings) {
        for (const id in crownTrimMoldings) {
          callback({ entityType, id } as BuildingEntityHandle);
        }
      }
      break;
    default:
      break;
  }
}
